import React, { useEffect, useState } from 'react';

// Styles & Assets
import logos from '../../../assets/common/logos.json';

// Services
import { getFullName } from '../../../services/getPatientAtribute';
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../hooks/useToast';
import useHttp from '../../../hooks/useHttp';

// Components
import GenerateQRDialog from '../../Helpers/GenerateQRDialog/GenerateQRDialog';

import { Patient } from '../../../interfaces/Patient';
// Interfaces 
import { IQRData } from '../../../interfaces/UtilInterfaces';
import ErrorSpinnerWrapper from '../../UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';

// Other
import moment from 'moment';

interface Props {
    patient?: Patient
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>
}

const PatientQRDialog: React.FC<Props> = ({ patient, setShowModal }) => {

    const { isLoading, error, sendRequest } = useHttp<IQRData>();

    const [qrData, setQrData] = useState<IQRData | null>(null);

    const [qrGeneratedAt, setQrGeneratedAt] = useState<string>();

    const { t } = useTranslation();

    const toast = useToast();

    // Fetch initial QR
    useEffect(() => {
        sendRequest({
            url: getEnvironmentApiUrl() + 'app_user_login/patient_auth_qr/' + patient?.id,
            method: 'GET'
        }, (data) => {
            setQrData(data);
        })
    }, [patient?.id, sendRequest]);

    // Regenerate QR
    const handleRegenerateQR = () => {
        if (patient?.id == null) return;
        sendRequest({
            url: getEnvironmentApiUrl() + 'app_user_login/regenerate_patient_qr/' + patient.id,
            method: 'POST'
        }, (data) => {
            setQrData(data); // update with the new QR
        });
    };

    // Update last update date when qrData changes
    useEffect(() => {
        if (qrData?.created_at) {
            setQrGeneratedAt(moment(qrData.created_at).format('DD-MM-YYYY HH:mm:ss'));
        }
    }, [qrData]);

    const handleGenerateQR = () => {

        import('jspdf').then(({ jsPDF }) => {

            const qrCode = document.getElementById('qr-code-patient') as HTMLCanvasElement;
            const doc = new jsPDF();

            doc.addImage(logos.logoBC, 'string', 14, 14, 50, 11);

            doc.setFont('helvetica', 'normal', 'bold');
            doc.setFontSize(19);
            doc.text(t('access_code_HomeConnect') as string, doc.internal.pageSize.getWidth() / 2, 50, { align: 'center' });

            if (qrCode) {
                doc.addImage(qrCode, 'HTMLCanvasElement', doc.internal.pageSize.getWidth() / 2 - 32, 65, 64, 64);
                doc.setFont('helvetica', 'normal', 400);
                doc.setFontSize(10);
                doc.text(`${t('created_on')}: ${qrGeneratedAt}`, doc.internal.pageSize.getWidth() / 2, 135, { align: 'center' });
            } else {
                doc.text(t('lost_QR') as string + ':', doc.internal.pageSize.getWidth() / 2, 90, { align: 'center' });
            }

            doc.setFont('helvetica', 'normal', 400);
            doc.setFontSize(12);
            doc.text(t('QR_user_identifier') as string + ':', doc.internal.pageSize.getWidth() / 2, 180, { align: 'center' });

            doc.setFont('helvetica', 'normal', 'bold');
            doc.setFontSize(19);
            doc.text(getFullName(patient), doc.internal.pageSize.getWidth() / 2, 195, { align: 'center' });

            doc.setFont('helvetica', 'normal', 400);
            doc.setFontSize(12);
            doc.text(t('log_identify') as string, doc.internal.pageSize.getWidth() / 2, 215, { align: 'center' });

            doc.setFontSize(10);

            doc.text(t('event_of_incident') as string, doc.internal.pageSize.getWidth() / 2, 230, { align: 'center', maxWidth: doc.internal.pageSize.getWidth() - 40 });
            doc.text(`${t('phone')}: ${window.config.PHONE_QR_FILE ?? '-'}`, doc.internal.pageSize.getWidth() / 2, 240, { align: 'center' });
            doc.text(`${t('email')}: ${window.config.EMAIL_QR_FILE ?? '-'}`, doc.internal.pageSize.getWidth() / 2, 245, { align: 'center' });
            doc.text(t('outside_office') as string, doc.internal.pageSize.getWidth() / 2, 250, { align: 'center' });

            doc.addImage(logos.logoBC, 'string', doc.internal.pageSize.getWidth() / 2 - 20.5, doc.internal.pageSize.getHeight() - 25, 41, 9);

            window.open(doc.output('bloburl'));

        }).catch(() => toast.error(t('resource_not_load')));

    };


    return (
        <ErrorSpinnerWrapper loading={isLoading} error={error}>
            <GenerateQRDialog
                handlePrintQR={handleGenerateQR}
                qrCode={qrData?.qr_code}
                setShowModal={setShowModal}
                onRegenerate={handleRegenerateQR}
                qrGeneratedAt={qrGeneratedAt}
            />
        </ErrorSpinnerWrapper>
    );
};

export default PatientQRDialog;